import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import ProcedureService from '../../services/procedureService';
import Button from '../common/Button';
import './Procedures.css';

const Procedures = () => {
  const auth = useAuth();
  const [procedures, setProcedures] = useState([]);
  const [filteredProcedures, setFilteredProcedures] = useState([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingProcedure, setEditingProcedure] = useState(null);
  const [deletingProcedure, setDeletingProcedure] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    cost: '',
    codigoSAT: '',
    taxes: {
      iva: {
        enabled: false,
        type: 'traslado',
        rate: null
      },
      isr: {
        enabled: false,
        type: 'retencion',
        rate: null
      }
    }
  });

  const loadProcedures = useCallback(async () => {
    try {
      setInitialLoading(true);
      const token = auth.user?.access_token;
      const response = await ProcedureService.getAllProcedures(token);
      setProcedures(response.data || []);
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Error al cargar los procedimientos'
      });
    } finally {
      setInitialLoading(false);
    }
  }, [auth.user?.access_token]);

  // Load procedures on component mount
  useEffect(() => {
    loadProcedures();
  }, [loadProcedures]);

  // Filter procedures when search filter or procedures change
  useEffect(() => {
    if (!searchFilter.trim()) {
      setFilteredProcedures(procedures);
    } else {
      const filtered = procedures.filter(procedure =>
        procedure.name.toLowerCase().includes(searchFilter.toLowerCase())
      );
      setFilteredProcedures(filtered);
    }
  }, [searchFilter, procedures]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTaxChange = (taxType, field, value) => {
    setFormData(prev => ({
      ...prev,
      taxes: {
        ...prev.taxes,
        [taxType]: {
          ...prev.taxes[taxType],
          [field]: value
        }
      }
    }));
  };

  const handleSearchChange = (e) => {
    setSearchFilter(e.target.value);
  };

  const clearSearch = () => {
    setSearchFilter('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = auth.user?.access_token;
      
      console.log('Submitting formData:', JSON.stringify(formData, null, 2));
      
      if (editingProcedure) {
        // Update existing procedure
        await ProcedureService.updateProcedure(editingProcedure.id, formData, token);
        setMessage({
          type: 'success',
          text: 'Procedimiento actualizado exitosamente'
        });
      } else {
        // Create new procedure
        await ProcedureService.createProcedure(formData, token);
        setMessage({
          type: 'success',
          text: 'Procedimiento creado exitosamente'
        });
      }

      // Reload procedures and close modal
      await loadProcedures();
      handleCloseModal();
    } catch (error) {
      setMessage({
        type: 'error',
        text: editingProcedure 
          ? 'Error al actualizar el procedimiento' 
          : 'Error al crear el procedimiento'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddProcedure = () => {
    setEditingProcedure(null);
    setFormData({
      name: '',
      cost: '',
      codigoSAT: '',
      taxes: {
        iva: { enabled: false, type: 'traslado', rate: null },
        isr: { enabled: false, type: 'retencion', rate: null }
      }
    });
    setShowModal(true);
    setMessage({ type: '', text: '' });
  };

  const handleEditProcedure = (procedure) => {
    setEditingProcedure(procedure);
    setFormData({
      name: procedure.name,
      cost: procedure.cost.toString(),
      codigoSAT: procedure.codigoSAT || '',
      taxes: procedure.taxes || {
        iva: { enabled: false, type: 'traslado', rate: null },
        isr: { enabled: false, type: 'retencion', rate: null }
      }
    });
    setShowModal(true);
    setMessage({ type: '', text: '' });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProcedure(null);
    setFormData({
      name: '',
      cost: '',
      codigoSAT: '',
      taxes: {
        iva: { enabled: false, type: 'traslado', rate: null },
        isr: { enabled: false, type: 'retencion', rate: null }
      }
    });
    setMessage({ type: '', text: '' });
  };

  const handleDeleteProcedure = (procedure) => {
    setDeletingProcedure(procedure);
    setShowDeleteModal(true);
    setMessage({ type: '', text: '' });
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletingProcedure(null);
    setMessage({ type: '', text: '' });
  };

  const confirmDelete = async () => {
    if (!deletingProcedure) return;

    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = auth.user?.access_token;
      await ProcedureService.deleteProcedure(deletingProcedure.id, token);
      
      setMessage({
        type: 'success',
        text: 'Procedimiento eliminado exitosamente'
      });

      // Reload procedures and close modal
      await loadProcedures();
      handleCloseDeleteModal();
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Error al eliminar el procedimiento'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const renderTaxChips = (taxes) => {
    if (!taxes) return <span className="no-taxes">-</span>;
    
    const chips = [];
    
    // IVA chip
    if (taxes.iva?.enabled) {
      let ivaLabel = 'IVA ';
      if (taxes.iva.rate === 'exento') {
        ivaLabel += 'Exento';
      } else if (taxes.iva.rate !== null) {
        ivaLabel += `${taxes.iva.rate}%`;
      }
      const ivaType = taxes.iva.type === 'traslado' ? 'T' : 'R';
      chips.push(
        <span key="iva" className={`tax-chip tax-chip-${taxes.iva.type}`} title={taxes.iva.type === 'traslado' ? 'Trasladado' : 'Retenido'}>
          {ivaLabel} ({ivaType})
        </span>
      );
    }
    
    // ISR chip
    if (taxes.isr?.enabled && taxes.isr.rate !== null) {
      const isrType = taxes.isr.type === 'traslado' ? 'T' : 'R';
      chips.push(
        <span key="isr" className={`tax-chip tax-chip-${taxes.isr.type}`} title={taxes.isr.type === 'traslado' ? 'Trasladado' : 'Retenido'}>
          ISR {taxes.isr.rate}% ({isrType})
        </span>
      );
    }
    
    return chips.length > 0 ? <div className="tax-chips-container">{chips}</div> : <span className="no-taxes">-</span>;
  };

  if (initialLoading) {
    return (
      <div className="procedures-container">
        <div className="procedures-loading">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="procedures-container">
      <div className="procedures-header">
        <div className="header-content">
          <h1>Procedimientos</h1>
          <p>Gestiona los procedimientos disponibles en tu clínica</p>
        </div>
        <button 
          className="btn-primary add-btn"
          onClick={handleAddProcedure}
        >
          Agregar Procedimiento
        </button>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Filters */}
      <div className="procedures-filters">
        <div className="search-container">
          <input
            type="text"
            placeholder="Buscar por nombre de procedimiento..."
            value={searchFilter}
            onChange={handleSearchChange}
            className="search-input"
          />
          {searchFilter && (
            <button
              className="clear-search-btn"
              onClick={clearSearch}
              title="Limpiar búsqueda"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Procedures Table */}
      <div className="procedures-table-container">
        {filteredProcedures.length === 0 ? (
          <div className="no-procedures">
            <p>No se encontraron procedimientos</p>
            {searchFilter && (
              <button className="btn-secondary" onClick={clearSearch}>
                Limpiar filtros
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <table className="procedures-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Costo</th>
                  <th>Impuestos</th>
                  <th>Codigo SAT</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredProcedures.map((procedure) => {
                  return (
                    <tr key={procedure.id}>
                      <td className="procedure-name">
                        <div className="procedure-details">
                          <span className="name">{procedure.name}</span>
                        </div>
                      </td>
                      <td className="procedure-cost">{formatCurrency(procedure.cost)}</td>
                      <td className="procedure-taxes">{renderTaxChips(procedure.taxes)}</td>
                      <td className="procedure-codigo-sat">
                        {procedure.codigoSAT || '-'}
                      </td>
                      <td className="procedure-actions">
                        <button
                          className="btn-secondary edit-btn"
                          onClick={() => handleEditProcedure(procedure)}
                          title="Editar procedimiento"
                        >
                          Editar
                        </button>
                        <button
                          className="btn-danger delete-btn"
                          onClick={() => handleDeleteProcedure(procedure)}
                          title="Eliminar procedimiento"
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="procedures-mobile-cards">
              {filteredProcedures.map((procedure) => (
                <div key={procedure.id} className="procedure-card">
                  <div className="procedure-card-header">
                    <div className="procedure-card-title">{procedure.name}</div>
                  </div>
                  <div className="procedure-card-content">
                    <div className="procedure-card-row">
                      <span className="procedure-card-label">Costo:</span>
                      <span className="procedure-card-value cost">{formatCurrency(procedure.cost)}</span>
                    </div>
                    <div className="procedure-card-row">
                      <span className="procedure-card-label">Impuestos:</span>
                      <span className="procedure-card-value">{renderTaxChips(procedure.taxes)}</span>
                    </div>
                    <div className="procedure-card-row">
                      <span className="procedure-card-label">Codigo SAT:</span>
                      <span className="procedure-card-value">
                        {procedure.codigoSAT || '-'}
                      </span>
                    </div>
                  </div>
                  <div className="procedure-card-actions">
                    <button
                      className="btn-secondary edit-btn"
                      onClick={() => handleEditProcedure(procedure)}
                      title="Editar procedimiento"
                    >
                      Editar
                    </button>
                    <button
                      className="btn-danger delete-btn"
                      onClick={() => handleDeleteProcedure(procedure)}
                      title="Eliminar procedimiento"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{editingProcedure ? 'Editar Procedimiento' : 'Agregar Nuevo Procedimiento'}</h3>
              <button className="modal-close" onClick={handleCloseModal}>
                ✕
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="name">
                    Nombre del Procedimiento <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Ej: Limpieza dental, Extracción, etc."
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="cost">
                    Costo (MXN) <span className="required">*</span>
                  </label>
                  <input
                    type="number"
                    id="cost"
                    name="cost"
                    value={formData.cost}
                    onChange={handleInputChange}
                    required
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="codigoSAT">
                    Codigo SAT (CFDI 4.0)
                  </label>
                  <input
                    type="text"
                    id="codigoSAT"
                    name="codigoSAT"
                    value={formData.codigoSAT}
                    onChange={handleInputChange}
                    placeholder="Ej: 85121800"
                  />
                </div>
              </div>

              {/* Tax Configuration Section */}
              <div className="tax-config-section">
                <h4>Configuración de Impuestos (CFDI 4.0)</h4>
                
                {/* IVA Configuration */}
                <div className="tax-group">
                  <div className="tax-header">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.taxes.iva.enabled}
                        onChange={(e) => handleTaxChange('iva', 'enabled', e.target.checked)}
                      />
                      <span className="tax-name">IVA (Impuesto al Valor Agregado)</span>
                    </label>
                  </div>
                  
                  {formData.taxes.iva.enabled && (
                    <div className="tax-details">
                      <div className="form-group-inline">
                        <label htmlFor="iva-type">Tipo:</label>
                        <select
                          id="iva-type"
                          value={formData.taxes.iva.type}
                          onChange={(e) => handleTaxChange('iva', 'type', e.target.value)}
                        >
                          <option value="traslado">Trasladado</option>
                          <option value="retencion">Retenido</option>
                        </select>
                      </div>
                      
                      <div className="form-group-inline">
                        <label htmlFor="iva-rate">Tasa:</label>
                        <select
                          id="iva-rate"
                          value={
                            formData.taxes.iva.rate === null
                              ? ''
                              : formData.taxes.iva.rate === 'exento'
                                ? 'exento'
                                : String(formData.taxes.iva.rate)
                          }
                          onChange={(e) => {
                            const val = e.target.value;
                            handleTaxChange('iva', 'rate', val === 'exento' ? 'exento' : (val === '' ? null : Number(val)));
                          }}
                        >
                          <option value="">Seleccionar</option>
                          <option value="exento">Exento</option>
                          <option value="0">0%</option>
                          <option value="8">8%</option>
                          <option value="16">16%</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>

                {/* ISR Configuration */}
                <div className="tax-group">
                  <div className="tax-header">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.taxes.isr.enabled}
                        onChange={(e) => handleTaxChange('isr', 'enabled', e.target.checked)}
                      />
                      <span className="tax-name">ISR (Impuesto Sobre la Renta)</span>
                    </label>
                  </div>
                  
                  {formData.taxes.isr.enabled && (
                    <div className="tax-details">
                      <div className="form-group-inline">
                        <label htmlFor="isr-type">Tipo:</label>
                        <select
                          id="isr-type"
                          value={formData.taxes.isr.type}
                          onChange={(e) => handleTaxChange('isr', 'type', e.target.value)}
                        >
                          <option value="retencion">Retenido</option>
                          <option value="traslado">Trasladado</option>
                        </select>
                      </div>
                      
                      <div className="form-group-inline">
                        <label htmlFor="isr-rate">Tasa (%):</label>
                        <input
                          type="number"
                          id="isr-rate"
                          value={formData.taxes.isr.rate || ''}
                          onChange={(e) => handleTaxChange('isr', 'rate', e.target.value ? Number(e.target.value) : null)}
                          min="0"
                          max="100"
                          step="0.01"
                          placeholder="Ej: 10, 10.67"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {editingProcedure && (
                <div className="form-info-section">
                  <div className="info-row">
                    <span className="info-label">Fecha de Creación:</span>
                    <span className="info-value">
                      {new Date(editingProcedure.createdAt).toLocaleString('es-MX', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Última Actualización:</span>
                    <span className="info-value">
                      {new Date(editingProcedure.updatedAt).toLocaleString('es-MX', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleCloseModal}
                  disabled={isLoading}
                >
                  Cancelar
                </button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={isLoading}
                  icon="fas fa-save"
                >
                  {isLoading
                    ? (editingProcedure ? 'Actualizando...' : 'Creando...')
                    : (editingProcedure ? 'Actualizar' : 'Crear')
                  }
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal delete-modal">
            <div className="modal-header">
              <h3>Confirmar Eliminación</h3>
              <button className="modal-close" onClick={handleCloseDeleteModal}>
                ✕
              </button>
            </div>
            
            <div className="modal-content">
              <p>
                ¿Estás seguro de que deseas eliminar el procedimiento{' '}
                <strong>"{deletingProcedure?.name}"</strong>?
              </p>
              <p className="warning-text">
                Esta acción no se puede deshacer.
              </p>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCloseDeleteModal}
                disabled={isLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={confirmDelete}
                disabled={isLoading}
              >
                {isLoading ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Procedures;
